import './Map.css'
import { useState, useEffect, useRef } from 'react'
import { Map, TileLayer, withLeaflet } from 'react-leaflet'
import OriginalVectorGrid from 'react-leaflet-vectorgrid'

import { useStructureBbox } from '../data/useStructureBbox'
import { StructMetric, StructPeriod, StructType, StructureBase, structConf } from '../lib/structures'
import { LoadingSpinner } from './spinners'
import type { LatLngBoundsLiteral } from 'leaflet'
import { getMetricNum } from '../lib/utils'
import PopUp from './PopUp'

const VectorGrid: any = withLeaflet(OriginalVectorGrid)

interface MapProps {
  type: StructType
  code: string
  childStructures: StructureBase[]
  childColourFn?: (code: string) => string
  childTypeOverride?: StructType
  metric: StructMetric
  period: StructPeriod
  showBoundaries: boolean
}

export interface PopupInfo {
  text?: string
  code?: string
  values?: Array<{metric: string, metricNumber: number}>
}

export default function ActiontMap (props: MapProps): JSX.Element {
  const {
    type,
    code,
    childStructures,
    childColourFn = () => '#b00000',
    childTypeOverride,
    metric,
    period,
    showBoundaries
  } = props
  const { loading, bbox } = useStructureBbox(type, code)
  const [nonce, setNonce] = useState<number>(Math.random())
  const [feature, setFeature] = useState<any>(null)
  const [popupPane, setPopupPane] = useState<HTMLElement | null | undefined>()
  const mapRef = useRef<any>(null)
  const tileRoot = 'https://tileserver.da-io.net'

  useEffect(() => {
    setNonce(Math.random())
  }, [childColourFn, childTypeOverride])

  useEffect(() => {
    setPopupPane(null)
  }, [metric, period, showBoundaries])

  useEffect(() => {
    const pane = document.querySelector('.leaflet-pane.leaflet-popup-pane') as HTMLElement
    setPopupPane(pane)
  }, [feature?.layerPoint])

  const onVectorGridClickCheck = (event: any) => {
    if (!event?.properties) setPopupPane(null)
  }

  const getInfo = (properties: { code: string, [key: string]: string }): PopupInfo | null => {
    if (!properties) return null
    const struct = childStructures.find(struct => struct.code === properties.code)
    const { name, code, type, canvassing, activity } = struct ?? {}
    const text = type === 'ward' || type === 'votingdistrict' ? `${name} (${code})` : name
    const metricNumber = getMetricNum(struct, metric, period)

    const values = metric === 'both'
      ? [{ metric: 'Canvassing', metricNumber: canvassing?.[period] },
          { metric: 'Activity', metricNumber: activity?.attendance[period] },
          { metric: 'Total', metricNumber: metricNumber }]
      : [{ metric, metricNumber }]

    return { text, code, values }
  }

  const childType = childTypeOverride ?? ((type === 'province' && (code === 'WC' || code === 'GP')) ? 'region' : structConf[type].mapChildren)
  let childLayer = {}
  if (childType != null) {
    const childLayerName = childType === 'votingdistrict' ? 'vdistrict' : childType
    const childCodes = childStructures.map(c => c.code)
    childLayer = {
      type: 'protobuf',
      url: `${tileRoot}/${childLayerName}/{z}/{x}/{y}/tile.mvt`,
      maxZoom: 16,
      vectorTileLayerStyles: {
        [childLayerName]: (properties: { code: string }) => {
          const show = childCodes.includes(properties.code)
          return {
            stroke: show,
            weight: 0.5,
            color: '#646464',
            fill: show,
            fillColor: childColourFn(properties.code),
            fillOpacity: 0.5
          }
        }
      },
      zIndex: 401,
      onClick: (feature: any) => {
        if (!feature) return
        const { layerPoint, layer } = feature ?? {}
        setFeature({ layerPoint, properties: layer?.properties })
        onVectorGridClickCheck(layer)
      },
      subdomains: 'abc',
      interactive: true
    }
  }

  if (loading) {
    return (
      <div className='action-map'>
        <LoadingSpinner />
      </div>
    )
  } else {
    const bounds = [
      [bbox?.south ?? 0, bbox?.west ?? 0],
      [bbox?.north ?? 0, bbox?.east ?? 0]
    ] as LatLngBoundsLiteral

    return (
      <div className='action-map'>
        <Map ref={mapRef} scrollWheelZoom bounds={bounds} onclick={event => onVectorGridClickCheck(event)}>
          <TileLayer
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            attribution='© <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {showBoundaries &&
            <TileLayer
              url='https://mapping.voteda.org/structures-2019/tiles/admin25/{z}/{x}/{y}.png'
              attribution='© <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              zIndex={1000}
            />}
          {childLayer != null ? <VectorGrid key={nonce} {...childLayer} /> : <></>}
          <PopUp popupPane={popupPane} setPopupPane={setPopupPane} data={getInfo(feature?.properties)} layerPoint={feature?.layerPoint} childType={childType} />
        </Map>
      </div>
    )
  }
}
